import {
  CircularProgress,
  CircularProgressProps,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

type InlineStatusProps = {
  textColor?:
    | 'initial'
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'textPrimary'
    | 'textSecondary'
    | 'error';
  progressColor?: CircularProgressProps['color'];
  sx?: SxProps;
  fullWidth?: boolean;
} & ({ status: 'loading' } | { status: 'error'; errorMessage?: string });

export const InlineStatus = (props: InlineStatusProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    status,
    textColor = 'error',
    progressColor,
    sx = {},
    fullWidth = false,
  } = props;

  const layoutSx: SxProps = fullWidth
    ? // height: 'fit-content' keeps the Stack sized to its content so it
      // doesn't stretch to fill a flex parent (which would otherwise expand
      // it well beyond the minHeight when rendered inside a flex container).
      { width: '100%', textAlign: 'center', p: 3, height: 'fit-content' }
    : {};

  if (status === 'loading') {
    return (
      <Stack
        spacing={0}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '20vh',
          ...layoutSx,
          ...sx,
        }}
      >
        <CircularProgress color={progressColor || 'primary'} />
      </Stack>
    );
  }

  const { errorMessage } = props;
  const errorText = errorMessage || t('common.unknownError');
  // log error with custom fingerprint to group similar errors together by the path they occur on
  Sentry.captureMessage('loading-and-error', {
    level: 'warning',
    extra: {
      errorText,
    },
    fingerprint: ['loading-and-error', window.location.pathname],
  });

  return (
    <Stack
      spacing={0}
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '10vh',
        ...layoutSx,
        ...sx,
      }}
    >
      <Typography
        color={textColor}
        align="center"
        variant="h3"
        sx={{ padding: 2 }}
      >
        {errorText}
      </Typography>
    </Stack>
  );
};
